import {
  ColumnDef,
  OnChangeFn,
  SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onPageChange: (change: -1 | 1) => void;
  pageCount: number;
  currentPage: number;
  total: number;
  resourceName: string;
  offset: number;
  limit: number;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  sorting,
  onSortingChange,
  hasNextPage,
  hasPreviousPage,
  currentPage,
  onPageChange,
  pageCount,
  total,
  resourceName,
  offset,
  limit,
}: DataTableProps<TData, TValue>) {
  // Normalize numeric props to prevent NaN
  const normalizedTotal = Number.isFinite(total) ? total : 0;
  const normalizedOffset = Number.isFinite(offset) ? offset : 0;
  const normalizedLimit = Number.isFinite(limit) ? limit : 10;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    manualSorting: true,
    state: {
      sorting,
    },
    pageCount,
  });

  return (
    <div className="h-full flex flex-col justify-between flex-grow">
      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="px-6 py-4">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onRowClick?.(row.original)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="px-6 py-4">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {normalizedTotal > 0 ? (
        <div className="flex items-center justify-between py-4">
          <div className="text-sm text-muted">
            Showing {normalizedOffset + 1}-
            {Math.min(normalizedOffset + normalizedLimit, normalizedTotal)} of {normalizedTotal}{' '}
            {resourceName}
          </div>
          <div className="flex items-center space-x-2">
            {pageCount > 0 && (
              <p className="text-sm text-muted">
                Page {currentPage} of {pageCount}
              </p>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(-1)}
              disabled={!hasPreviousPage}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={!hasNextPage}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
